import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export default function AdminBrandsList() {
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [crawling, setCrawling] = useState(null); // brand_id currently being force-crawled
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();

  // Pre-fill payload for the New Brand dialog when the operator arrives
  // from the Discovery dry-run "Save as new brand" button. The dry-run
  // ships the verified URL + seed URLs on the query string so nothing
  // gets re-typed and nothing gets lost.
  const prefill = (() => {
    const url = sp.get("prefill_url") || "";
    const seedsRaw = sp.get("prefill_seeds") || "";
    let seeds = [];
    if (seedsRaw) {
      try { seeds = JSON.parse(seedsRaw); if (!Array.isArray(seeds)) seeds = []; }
      catch { seeds = []; }
    }
    return { website_url: url, extra_seed_urls: seeds };
  })();
  useEffect(() => {
    if (sp.get("open") === "1") {
      setShowNew(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onCreated = (slug) => {
    // Strip the prefill params so a subsequent close/open doesn't
    // re-hydrate the form with stale values.
    setSp({});
    nav(`/admin/brands/${slug}`);
  };

  const load = useCallback(
    () => http.get("/admin/brands").then((r) => setBrands(r.data.brands || [])),
    [],
  );
  useEffect(() => { load(); }, [load]);

  const filtered = brands.filter((b) => !q || b.display_name.toLowerCase().includes(q.toLowerCase()) || b.slug.toLowerCase().includes(q.toLowerCase()));

  const forceCrawl = async (b) => {
    setCrawling(b.id);
    try {
      const { data } = await http.post(`/admin/pipeline/reprocess/${b.id}`);
      toast.success(`Crawl re-queued for ${b.display_name} (run ${data.run_id.slice(0, 8)}…)`);
    } catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail;
        const state = typeof d === "object" ? d.state : null;
        toast.message(
          `${b.display_name}: run already in flight${state ? ` (${state})` : ""}.`,
        );
      } else {
        toast.error(e.response?.data?.detail || "Force crawl failed");
      }
    } finally { setCrawling(null); }
  };

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-4xl">Brands</h1>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button className="rounded-none" data-testid="brands-new">+ New brand</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">Create brand</DialogTitle></DialogHeader>
              <NewBrandForm
                initial={prefill}
                onCreated={(slug) => {
                  setShowNew(false);
                  toast.success("Brand created");
                  onCreated(slug);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
        <div className="mb-4 max-w-md">
          <Input placeholder="Filter by name or slug…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="brands-filter" />
        </div>
        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Brand</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Website</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Sector</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Status</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Review</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Age</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                let host = "";
                try { host = b.website_url ? new URL(b.website_url).hostname.replace(/^www\./, "") : ""; }
                catch { host = b.website_url || ""; }
                return (
                <tr key={b.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`brands-row-${b.slug}`}>
                  <td className="px-4 py-3">
                    <div className="font-serif">{b.display_name}</div>
                    <div className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>{b.slug}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" data-testid={`brands-website-${b.slug}`}>
                    {host ? (
                      <a
                        href={b.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bp-link break-all"
                        title={b.website_url}
                      >
                        {host}
                      </a>
                    ) : (
                      <span style={{ color: "#B91C1C" }} title="Missing website — crawler has no seed">— missing</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{b.sector_code}</td>
                  <td className="px-4 py-3 font-mono text-xs">{b.status}</td>
                  <td className="px-4 py-3 font-mono text-xs">{b.profile?.review_state || "—"}</td>
                  <td className={`px-4 py-3 font-mono text-xs ${b.stale ? "text-amber-700" : ""}`}>{b.age_days ?? "—"}d{b.stale && " · stale"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => forceCrawl(b)}
                        disabled={crawling === b.id}
                        className="text-xs bp-link inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Force crawl now — enqueue a fresh pipeline run for this brand"
                        data-testid={`force-crawl-${b.slug}`}
                      >
                        <RefreshCw size={12} className={crawling === b.id ? "animate-spin" : ""} />
                        {crawling === b.id ? "Queuing…" : "Crawl"}
                      </button>
                      <Link to={`/admin/brands/${b.slug}`} className="bp-link" data-testid={`edit-${b.slug}`}>Edit →</Link>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function NewBrandForm({ onCreated, initial }) {
  const [f, setF] = useState({
    slug: "",
    legal_name: "",
    display_name: "",
    sector_code: "FASHION",
    country: "IT",
    hq_city: "",
    website_url: initial?.website_url || "",
    short_description_en: "",
    short_description_it: "",
    // Pre-filled from Discovery dry-run when the operator hits
    // "Save as new brand" — otherwise empty. One URL per line.
    extra_seed_urls: (initial?.extra_seed_urls || []).join("\n"),
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const seeds = (f.extra_seed_urls || "")
        .split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      const payload = { ...f, extra_seed_urls: seeds };
      await http.post("/admin/brands", payload);
      onCreated(f.slug);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs uppercase tracking-widest">Slug</label><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="new-brand-slug" /></div>
        <div><label className="text-xs uppercase tracking-widest">Display name</label><Input required value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} data-testid="new-brand-name" /></div>
      </div>
      <div><label className="text-xs uppercase tracking-widest">Legal name</label><Input required value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} data-testid="new-brand-legal" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest">Sector</label>
          <select className="w-full border p-2" value={f.sector_code} onChange={(e) => setF({ ...f, sector_code: e.target.value })} style={{ borderColor: "var(--bp-border)" }} data-testid="new-brand-sector">
            <option value="FASHION">FASHION</option>
            <option value="FURNITURE">FURNITURE</option>
          </select>
        </div>
        <div><label className="text-xs uppercase tracking-widest">Country (ISO2)</label><Input required maxLength={2} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })} data-testid="new-brand-country" /></div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest">Official website</label>
        <Input
          value={f.website_url}
          onChange={(e) => setF({ ...f, website_url: e.target.value })}
          placeholder="e.g. brunellocucinelli.com"
          data-testid="new-brand-web"
        />
        {/* Live URL preview — shows exactly how the backend will
            normalize this input BEFORE the operator hits Create. Same
            rules as _normalize_website in admin.py: prepend https://
            if missing + strip leading www. for official_domains. */}
        {(() => {
          const raw = (f.website_url || "").trim();
          if (!raw) {
            return (
              <div
                className="mt-1 text-[11px] font-mono"
                style={{ color: "#B91C1C" }}
                data-testid="new-brand-web-preview-empty"
              >
                Empty — brand will be created but NO pipeline run will be enqueued.
              </div>
            );
          }
          const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
          let host = "";
          try { host = new URL(withScheme).hostname.replace(/^www\./, ""); } catch { host = ""; }
          return (
            <div
              className="mt-1 text-[11px] font-mono"
              style={{ color: "var(--bp-text-muted)" }}
              data-testid="new-brand-web-preview"
            >
              Will be saved as: <span style={{ color: "#166534" }}>{withScheme}</span>
              {host && (
                <>
                  {" · "}official domain:{" "}
                  <span style={{ color: "#166534" }}>{host}</span>
                </>
              )}
            </div>
          );
        })()}
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest">
          Extra seed URLs
          <span className="ml-2 lowercase tracking-normal text-[10px]">optional · one per line · pushed into discovery</span>
        </label>
        <textarea
          rows={3}
          className="w-full border p-2 font-mono text-xs"
          style={{ borderColor: "var(--bp-border)" }}
          value={f.extra_seed_urls}
          onChange={(e) => setF({ ...f, extra_seed_urls: e.target.value })}
          placeholder="https://acme.com/about"
          data-testid="new-brand-seeds"
        />
        {initial?.extra_seed_urls?.length > 0 && (
          <div
            className="mt-1 text-[10px]"
            style={{ color: "#166534" }}
            data-testid="new-brand-seeds-prefilled"
          >
            ✓ Pre-filled from Discovery dry-run ({initial.extra_seed_urls.length} URL{initial.extra_seed_urls.length === 1 ? "" : "s"})
          </div>
        )}
      </div>
      <Button type="submit" disabled={busy} className="rounded-none w-full" data-testid="new-brand-submit">{busy ? "Creating…" : "Create brand"}</Button>
    </form>
  );
}
